import type { BlogTileData } from '@bulknutrients/bulk-shared-lib/interfaces/Blog';
import { getBlogTiles } from '@bulknutrients/bulk-shared-lib/lib/queries/sanity/blogs';
import { cdnClient as client } from '@bulknutrients/bulk-shared-lib/lib/sanity/client';
import { formatUrl } from '@bulknutrients/bulk-shared-lib/utils';
import { ErrorCard } from '@components/Alerts';
import { LoadMore } from '@components/LoadMore/LoadMore';
import type { ReactElement } from 'react';
import useSWRInfinite from 'swr/infinite';

import { BlogCard, LoadingBlogCard } from './BlogCard';

export function BlogCardList({
  category = '',
  author = '',
}: {
  category?: string;
  author?: string;
}): ReactElement {
  const PAGE_SIZE = 45;

  // A function to get the SWR key of each page,
  // its return value will be accepted by `fetcher`.
  // If `null` is returned, the request of that page won't start.
  const getKey = (pageIndex, previousPageData) => {
    if (previousPageData && !previousPageData.data.length) return null; // reached the end
    return `/api/blog/getBlogTiles?pageIndex=${pageIndex}&pageSize=${PAGE_SIZE}&category=${category}&author=${author}`; // SWR key
  };

  const fetcher = async (url) => {
    const fullUrl = new URL(url, 'https://www.bulk.com');
    const pageIndex = fullUrl.searchParams.get('pageIndex');
    const author = fullUrl.searchParams.get('author');
    const category = fullUrl.searchParams.get('category');

    const start = Number.parseInt(pageIndex as string) * PAGE_SIZE;
    const end = start + PAGE_SIZE;

    /* 
      get blog tiles by category if category exists in query
      otherwise, if category does not exist, get all blog tiles
    */
    try {
      const { data, count } = await client.fetch(
        `{
          "data": ${getBlogTiles},
          "count": count(${getBlogTiles.slice(0, getBlogTiles.lastIndexOf('}') + 1)}),
        }`,
        {
          start,
          end,
          category: category,
          author: author,
        }
      );

      for (const blog of data) blog.slug = formatUrl(blog.slug);

      return { data, count };
    } catch (err) {
      const error = new Error('Unable to fetch blog tiles');
      throw error;
    }
  };

  const { data, error, isLoading, size, setSize } = useSWRInfinite(
    getKey,
    fetcher
  );

  const blogs: Array<BlogTileData> = data
    ? [...data.flatMap((d) => d.data)]
    : [];

  const isLoadingMore =
    isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isEmpty = data?.[0]?.data.length === 0;
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1]?.data.length < PAGE_SIZE);

  return (
    <>
      {error && <ErrorCard title={error.name} message={error.message} />}
      {blogs && (
        <section className="mb-8 grid justify-items-center grid-cols-1 gap-9 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {isLoading
            ? [...Array(9)].map((_, idx) => (
                <LoadingBlogCard key={`loading-blog-card-${idx}`} />
              ))
            : blogs.map((blog, idx) => (
                <BlogCard key={`blog${idx}`} blog={blog} />
              ))}
        </section>
      )}
      <LoadMore
        clickHandler={() => setSize(size + 1)}
        disabled={(isLoadingMore || isReachingEnd) ?? false}
        text={
          isLoadingMore
            ? 'loading...'
            : isReachingEnd
              ? 'no more results'
              : 'load more'
        }
        viewedResults={blogs.length ?? 0}
        totalResults={data?.[0]?.count}
      />
    </>
  );
}
